import { useEffect, useState } from 'react';
import { Wallet, AlertTriangle } from 'lucide-react';
import { request } from '@/api';

// Matches the server response from GET /api/llm/balance/deepseek
// (src/server/routes/balance.ts): { ok, provider, isAvailable, currency,
//  totalBalance, grantedBalance, toppedUpBalance, fetchedAt, cached }
interface DeepSeekBalanceData {
  ok: boolean;
  cached?: boolean;
  provider?: string;
  isAvailable?: boolean;
  currency?: string | null;
  totalBalance?: number | null;
  grantedBalance?: number | null;
  toppedUpBalance?: number | null;
  fetchedAt?: string;
  error?: string;
}

const POLL_MS = 60_000;

export default function DeepSeekBalance() {
  const [data, setData] = useState<DeepSeekBalanceData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const r = await request<DeepSeekBalanceData>('/api/llm/balance/deepseek');
        if (!active) return;
        setData(r);
        setError(null);
      } catch (e: any) {
        if (!active) return;
        setError(e.message || 'unavailable');
        setData(null);
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => {
      active = false;
      clearInterval(t);
    };
  }, []);

  // Not configured / unreachable — show nothing to keep the header clean
  if (error && !data) return null;
  if (!data || !data.ok) return null;

  const currency = data.currency || 'USD';
  const total = data.totalBalance ?? 0;
  const granted = data.grantedBalance ?? 0;
  const toppedUp = data.toppedUpBalance ?? 0;
  const low = data.isAvailable === false || total <= 0;

  const fmt = (n: number) => n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  const symbol = currency === 'CNY' ? '¥' : currency === 'USD' ? '$' : `${currency} `;

  return (
    <div
      className="hidden sm:flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border transition"
      title={`DeepSeek API balance — ${fmt(total)} ${currency} (granted ${fmt(granted)}, topped up ${fmt(toppedUp)}) · refreshes every 60s`}
    >
      {low ? (
        <AlertTriangle size={14} className="text-amber-500" />
      ) : (
        <Wallet size={14} className="text-emerald-500" />
      )}
      <span className={low ? 'text-amber-600' : 'text-gray-700 dark:text-slate-300'}>
        {symbol}
        {fmt(total)}
      </span>
    </div>
  );
}
